import { computed, signal, Signal, WritableSignal } from '@angular/core';
import { FilterOption, TableFilterConfig } from './table-filter.interfaces';

/** Metadata for a single dropdown field, for generic filter-bar UI to iterate over. */
export interface FilterFieldMeta {
  key: string;
  labelKey: string;
  mobileSubpage?: boolean;
  subpageSearchPlaceholder?: string;
}

/** Generic, per-table-configurable filtering state: free-text search + per-field multi-select. */
export interface TableFilterState<TItem> {
  readonly searchQuery: Signal<string>;
  /** Metadata for every configured dropdown field, in config order. */
  readonly fields: FilterFieldMeta[];
  /** Items after applying the free-text search and all field selections (AND across fields). */
  readonly filteredItems: Signal<TItem[]>;
  /** Whether the search query is non-empty or any field has at least one selected value. */
  readonly hasActiveFilters: Signal<boolean>;
  setSearchQuery(query: string): void;
  /** Current selectable options for a field, recomputed whenever the source items change. */
  getFieldOptions(fieldKey: string): Signal<FilterOption[]>;
  getSelectedValues(fieldKey: string): Signal<ReadonlySet<string>>;
  getSelectedCount(fieldKey: string): Signal<number>;
  toggleValue(fieldKey: string, value: string): void;
  setValues(fieldKey: string, values: string[]): void;
  clearField(fieldKey: string): void;
  /** Resets the free-text search and every field's selection. */
  clearAll(): void;
}

/**
 * Builds a reusable filter-state engine for a table from a `TableFilterConfig`, so each table's
 * store can delegate to this instead of reimplementing filter signals/computed logic per table.
 */
export function createTableFilterState<TItem>(
  config: TableFilterConfig<TItem>,
  items: Signal<TItem[]>
): TableFilterState<TItem> {
  const searchQuery = signal('');
  const selections = new Map<string, WritableSignal<Set<string>>>(
    config.fields.map(field => [field.key, signal(new Set<string>())])
  );
  const fieldOptions = new Map<string, Signal<FilterOption[]>>(
    config.fields.map(field => [field.key, computed(() => field.getOptions(items()))])
  );
  const selectedCounts = new Map<string, Signal<number>>(
    config.fields.map(field => [field.key, computed(() => getSelection(field.key)().size)])
  );
  const searchFields = config.searchFields ?? [];

  function getSelection(fieldKey: string): WritableSignal<Set<string>> {
    const selection = selections.get(fieldKey);
    if (!selection) {
      throw new Error(`Unknown filter field: ${fieldKey}`);
    }
    return selection;
  }

  const filteredItems = computed(() => {
    const query = searchQuery().toLowerCase().trim();

    return items().filter(item => {
      for (const field of config.fields) {
        const selected = getSelection(field.key)();
        if (selected.size === 0) continue;
        if (!field.getItemValues(item).some(value => selected.has(value))) {
          return false;
        }
      }

      if (query && searchFields.length > 0) {
        const matchesSearch = searchFields.some(searchField =>
          searchField.getSearchableText(item).toLowerCase().includes(query)
        );
        if (!matchesSearch) {
          return false;
        }
      }

      return true;
    });
  });

  const hasActiveFilters = computed(
    () =>
      searchQuery().trim().length > 0 ||
      [...selections.values()].some(selection => selection().size > 0)
  );

  return {
    searchQuery: searchQuery.asReadonly(),
    fields: config.fields.map(field => ({
      key: field.key,
      labelKey: field.labelKey,
      mobileSubpage: field.mobileSubpage,
      subpageSearchPlaceholder: field.subpageSearchPlaceholder,
    })),
    filteredItems,
    hasActiveFilters,
    setSearchQuery: query => searchQuery.set(query),
    getFieldOptions: fieldKey => {
      const options = fieldOptions.get(fieldKey);
      if (!options) {
        throw new Error(`Unknown filter field: ${fieldKey}`);
      }
      return options;
    },
    getSelectedValues: fieldKey => getSelection(fieldKey).asReadonly(),
    getSelectedCount: fieldKey => {
      const count = selectedCounts.get(fieldKey);
      if (!count) {
        throw new Error(`Unknown filter field: ${fieldKey}`);
      }
      return count;
    },
    toggleValue: (fieldKey, value) => {
      getSelection(fieldKey).update(current => {
        const next = new Set(current);
        if (next.has(value)) {
          next.delete(value);
        } else {
          next.add(value);
        }
        return next;
      });
    },
    setValues: (fieldKey, values) => {
      getSelection(fieldKey).set(new Set(values));
    },
    clearField: fieldKey => {
      getSelection(fieldKey).set(new Set());
    },
    clearAll: () => {
      searchQuery.set('');
      for (const selection of selections.values()) {
        selection.set(new Set());
      }
    },
  };
}
